import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import Routes from '../../../routes';
import UserInfo from '../../../UserInfo';
import {
    DateInput,
    Select,
    MultiSelect,
    PageTitleWithToolTip,
    PrimaryButton,
    LoadingButtonV4 as LoadingButton,
    InputNumber
} from '../../Common';
import { useNavigate } from '../../../hooks';
import { isDesktop, postRequestHeaders } from '../../../utils';
import { Grid, Paper } from '@mui/material';
import { trakrefBlue, trakrefYellow } from "../../../color_utils";
import Enums from "../../../enums";
import { renderWeight } from "../../../Utils/weight_utils";
import { formatDateWithZ } from '../../../utils';
import SiteServiceLayout from '../Shared/SiteServiceLayout';
import SystemRow from '../Shared/SystemRow';
import SystemFilterBar from '../Shared/SystemFilterBar';
import SystemSelectionControls from '../Shared/SystemSelectionControls';
import CompletionModal from '../Shared/CompletionModal';
import LeakFields from './LeakFields';
import { paperResponsiveStyle } from '../Shared/styles';

const inspectionTypes = [{ id: 1, label: "Leak Inspection" }];
const systemTypeFilters = [
    { id: 31, name: "Comfort Cooling" },
    { id: 63, name: "Process Cooling" },
    { id: 22, name: "Refrigeration" }
];
const requiredFields = [
    "inspectionDate",
    "leakLocationId",
    "leakSublocationId",
    "faultCauseTypeId",
];

const weightPrecision = UserInfo.getInstance(UserInfo.getInstanceID()) ? UserInfo.getInstance(UserInfo.getInstanceID()).weightPrecision : null;

const getChipData = (asset) => {
    const {
        coolingAppliance: { coolingApplianceStatusId = null } = {},
        isChecked,
        workItemDetail: { wasLeakFound } = {}
    } = asset;
    let chipData = {
        chipLabel: "No Change",
        color: "#FFF",
        chipColor: trakrefBlue,
        disabled: false
    };

    if (coolingApplianceStatusId === Enums.SE.CoolingApplianceStatus.UnderRepair) {
        chipData = {
            chipLabel: "Under Repair",
            chipColor: trakrefYellow,
            disabled: true
        };
    }

    if (isChecked && [null, undefined].includes(wasLeakFound)) {
        chipData.chipLabel = "Pending";
    }

    if (isChecked && wasLeakFound === false) {
        chipData.chipLabel = "No Leak Found";
    }

    if (isChecked && wasLeakFound === true) {
        chipData.chipLabel = "Leak Found";
    }

    return chipData;
};

const buildInventoryData = (asset) => {
    const {
        tagNumber = '',
        serialNumber = '',
        installDate = '',
        assetType: { name: assetTypeName = '' } = {},
        coolingAppliance: {
            gasChargeCapacityLbs = null,
            materialType: { name: materialTypeName = '' } = {},
        } = {},
    } = asset;

    return [
        { label: "System Type", value: assetTypeName },
        { label: "Tag Number", value: tagNumber },
        { label: "Gas Capacity", value: renderWeight(gasChargeCapacityLbs, weightPrecision || 3, weightPrecision || 2) },
        { label: "Material Type", value: materialTypeName },
        { label: "Serial Number", value: serialNumber },
        { label: "Install Date", value: formatDateWithZ(installDate) }
    ];
};

const SiteLeakInspection = (props) => {
    const workOrderId = props.match.params.id;
    const [inspectionDate, setInspectionDate] = useState(null);
    const [leakInspectionMethod, setLeakInspectionMethod] = useState(null);
    const [primaryTechnician, setPrimaryTechnician] = useState(null);
    const [systemTypes, setSystemTypes] = useState([
        Enums.SE.AssetType.ComfortCooling,
        Enums.SE.AssetType.ProcessCooling,
        Enums.SE.AssetType.Refrigeration
    ]);
    const [minimumChargeCapacity, setMinimumChargeCapacity] = useState('');
    const [assets, setAssets] = useState([]);
    const [originalAssets, setOriginalAssets] = useState([]);
    const [modalOpen, setModalOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [dropDownOptions, setDropDownOptions] = useState({
        technicians: [],
        leakLocations: [],
        leakLocationCategories: [],
        leakDetectionMethods: [],
        leakCauses: []
    });
    const navigate = useNavigate({ pageName: 'SiteLeakInspection' });

    const fetchData = async () => {
        const response = await fetch(Routes.ServiceEvent.SiteLeakInspectionDropdown(workOrderId));
        const info = await response.json();
        const {
            leakLocations,
            leakLocationCategories,
            leakDetectionMethods,
            leakCauses,
            siteLeak_Technicians,
            coolingAppliance
        } = info;

        const technicians = siteLeak_Technicians.filter((obj) => obj.validCertificateNumber === true);
        const assets = coolingAppliance.map(({ asset }) => ({ ...asset, workItemDetail: {} }));

        setAssets(assets);
        setOriginalAssets(assets);
        setDropDownOptions({
            technicians,
            leakLocations,
            leakLocationCategories,
            leakDetectionMethods,
            leakCauses
        });
    };

    const getFilteredData = () => {
        let originalAssetsClone = [...originalAssets].map(a => ({ ...a, isChecked: false }));
        
        if (systemTypes.length < systemTypeFilters.length) {
            originalAssetsClone = originalAssetsClone.filter(a => systemTypes.includes(a.assetType.id));
        }

        if (minimumChargeCapacity) {
            originalAssetsClone = originalAssetsClone.filter(a =>
                a.coolingAppliance.gasChargeCapacityLbs >= parseFloat(minimumChargeCapacity)
            );
        }

        setAssets(originalAssetsClone);
    };

    const isSaveDisabled = () => {
        if (!inspectionDate || !leakInspectionMethod || !primaryTechnician) {
            return true;
        }

        const includedAssets = assets.filter(a => a.isIncludedInLeakInspection);
        if (includedAssets.length === 0) {
            return true;
        }

        const savedDisabled = assets
            .filter(a => a.isChecked)
            .map((a) => {
                const { isChecked, isIncludedInLeakInspection, workItemDetail, workItemDetail: { wasLeakFound } } = a;

                if (isChecked && isIncludedInLeakInspection && !wasLeakFound) {
                    return false;
                }

                const allRequiredFieldsAnswered = requiredFields.every(field => !!workItemDetail[field]);
                if (
                    isChecked
                    && isIncludedInLeakInspection
                    && wasLeakFound
                    && allRequiredFieldsAnswered
                ) {
                    return false;
                }

                return true;
            });

        return savedDisabled.includes(true);
    };

    const handleSelectAll = () => {
        const checkedAssets = assets.map((a) =>
            ({
                ...a,
                isChecked: !a.isChecked &&
                    a.coolingAppliance.coolingApplianceStatusId !== Enums.SE.CoolingApplianceStatus.UnderRepair
            })
        );
        return setAssets(checkedAssets);
    };

    const handleDeselectAll = () => {
        const sanitizedAssets = assets.map((a) => {
            const { wasLeakFound, isIncludedInLeakInspection, isChecked, workItemDetail, ...rest } = a;
            return { ...rest };
        });
        return setAssets(sanitizedAssets);
    };

    const handleMarkAsNoLeak = () => {
        const selectedAssets = [...assets].map(a => ({
            ...a,
            ...(a.isChecked && {
                isIncludedInLeakInspection: true,
                workItemDetail: { ...a.workItemDetail, wasLeakFound: false },
            })
        }));
        return setAssets(selectedAssets);
    };

    const handleInspectionDate = (newDate) => {
        setInspectionDate(newDate);
        const assetsClone = assets
            .map(a => ({
                ...a,
                workItemDetail: {
                    ...a.workItemDetail,
                    ...(a.workItemDetail.wasLeakFound && { inspectionDate: newDate })
                }
            }));
        setAssets(assetsClone);
    };

    const handleCheck = (index, e) => {
        const isChecked = e.target.checked;
        const assetsClone = [...assets];
        assetsClone[index].isChecked = isChecked;

        if (!isChecked) {
            const { isChecked: _ic, isIncludedInLeakInspection, workItemDetail, ...rest } = assetsClone[index];
            assetsClone[index] = { ...rest, workItemDetail: {} };
        }

        setAssets(assetsClone);
    };

    const handleLeakFound = (index, leakFound) => {
        const assetsClone = [...assets];
        assetsClone[index] = {
            ...assetsClone[index],
            isIncludedInLeakInspection: true,
            workItemDetail: {
                wasLeakFound: leakFound,
                ...(leakFound && inspectionDate && { inspectionDate }),
            }
        };
        setAssets(assetsClone);
    };

    const setAssetField = (index, field, value) => {
        const assetsClone = [...assets];
        assetsClone[index]["workItemDetail"][field] = value;
        if (!value) {
            const { [field]: removedKey, ...rest } = assetsClone[index]["workItemDetail"];
            assetsClone[index]["workItemDetail"] = { ...rest };
        }
        setAssets(assetsClone);
    };

    const formatSubmissionData = (data) => ({
        ID: workOrderId,
        InstanceId: UserInfo.instanceId,
        LeakDetectionMethodId: leakInspectionMethod,
        PrimaryTechnicianId: primaryTechnician,
        ServiceDate: inspectionDate,
        UserId: UserInfo.userId,
        WorkItemTypeId: 2,
        WorkItems: [...data]
    });

    const submit = async () => {
        setLoading(true);
        const assetsToSubmit = formatSubmissionData(assets.filter(a => a.isIncludedInLeakInspection));
        console.log('assetsToSubmit', assetsToSubmit)

        const response =  await fetch(Routes.ServiceEvent.AddSiteLeakInspection(), {
            method: 'POST',
            headers: { ...postRequestHeaders },
            body: JSON.stringify(assetsToSubmit)
        });

        if (response.status === 200) {
            setLoading(false);
            return setModalOpen(true);
        }

        setLoading(false);
        return toast.error('There was an error saving the asset', { duration: 6000 });
    };

    useEffect(() => {
        fetchData();
    }, []);

    useEffect(() => {
        getFilteredData();
    }, [systemTypes, minimumChargeCapacity]);

    return (
        <SiteServiceLayout
            title={`Work Order #${workOrderId}`}
            onClose={() => navigate.goBack(`/WorkOrder/${workOrderId}`)}
            actionButton={
                <LoadingButton
                    useLargeButton
                    buttonText="Complete"
                    fullWidth={!isDesktop}
                    disabled={isSaveDisabled()}
                    loading={loading}
                    onClick={submit}
                />
            }
            alert={{ message: "It is recommended each leak location on an appliance is recorded as a separate leak ticket." }}
        >
            <Paper variant="outlined" sx={{ ...paperResponsiveStyle }}>
                <Grid container spacing={2}>
                    <Grid item xs={12} mt={2}>
                        <PageTitleWithToolTip pageTitle="Inspection Details" />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Select
                            disabled
                            value={inspectionTypes[0].id}
                            label="Inspection Type"
                            variant="outlined"
                            options={inspectionTypes}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <DateInput
                            variant="outlined"
                            allowPast
                            allowFuture={false}
                            label="Inspection Date *"
                            date={inspectionDate}
                            onChange={handleInspectionDate}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Select
                            required
                            value={leakInspectionMethod}
                            label="Leak Inspection Method"
                            variant="outlined"
                            options={dropDownOptions.leakDetectionMethods}
                            onChange={(e) => setLeakInspectionMethod(e.target.value)}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Select
                            required
                            value={primaryTechnician}
                            label="Primary Technician"
                            variant="outlined"
                            options={dropDownOptions.technicians}
                            onChange={(e) => setPrimaryTechnician(e.target.value)}
                        />
                    </Grid>
                </Grid>
            </Paper>

            <SystemFilterBar>
                <Grid item xs={12} md={6}>
                    <MultiSelect
                        renderAllOption
                        value={systemTypes}
                        label="System Type"
                        variant="outlined"
                        options={systemTypeFilters}
                        onChange={(e) => setSystemTypes(e.target.value)}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <InputNumber
                        fullWidth
                        hideStepper
                        allowDecimal
                        label="Charge Capacity (lbs)"
                        variant="outlined"
                        value={minimumChargeCapacity}
                        onChange={(e) => setMinimumChargeCapacity(e.target.value)}
                    />
                </Grid>
            </SystemFilterBar>

            <SystemSelectionControls
                hasSelection={assets.some(a => a.isChecked)}
                onSelectAll={handleSelectAll}
                onDeselectAll={handleDeselectAll}
                disabled={loading}
                bulkAction={
                    <PrimaryButton
                        buttonText={"Mark Selected As No Leak"}
                        onClick={handleMarkAsNoLeak}
                        disabled={loading}
                    />
                }
                helpText="Select each system included in the inspection and identify whether leak found or no leak found"
            />

            <Grid
                container
                spacing={2}
                sx={{
                    mt: 2,
                    mb: { xs: 2, md: 0 },
                }}
            >
                {assets.map((asset, index) => {
                    const { chipLabel, chipColor, disabled } = getChipData(asset);
                    const inventoryData = buildInventoryData(asset);
                    return (
                        <Grid item xs={12} key={index}>
                            <SystemRow
                                asset={asset}
                                isChecked={asset.isChecked || false}
                                onCheck={(e) => handleCheck(index, e)}
                                disabled={disabled}
                                loading={loading}
                                chipLabel={chipLabel}
                                chipColor={chipColor}
                                inventoryData={inventoryData}
                            >
                                <LeakFields
                                    isChecked={asset.isChecked || false}
                                    disabled={disabled}
                                    wasLeakFound={asset.workItemDetail?.wasLeakFound ?? null}
                                    inspectionDate={asset.workItemDetail?.inspectionDate ?? null}
                                    leakLocationId={asset.workItemDetail?.leakLocationId || ''}
                                    leakSublocationId={asset.workItemDetail?.leakSublocationId || ''}
                                    faultCauseTypeId={asset.workItemDetail?.faultCauseTypeId || ''}
                                    estimatedLeakAmount={asset.workItemDetail?.estimatedLeakAmount || ''}
                                    onLeakFound={(val) => handleLeakFound(index, val)}
                                    onFieldChange={(field, value) => setAssetField(index, field, value)}
                                    dropDownOptions={dropDownOptions}
                                />
                            </SystemRow>
                        </Grid>
                    );
                })}
            </Grid>

            <CompletionModal
                open={modalOpen}
                onClose={() => navigate.goBack(`/WorkOrder/${workOrderId}`)}
                header={"Site Leak Inspection created."}
                imageAlt={"Site Leak Inspection successfully created."}
                message={"If any leaks were repaired during this visit, remember to log them in a Leak Repair service record."}
                actionButtonText="VIEW WORK ORDER"
                onAction={() => navigate.goBack(`/WorkOrder/${workOrderId}`)}
            />
        </SiteServiceLayout>
    );
};

export default SiteLeakInspection;
